import React from 'react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { diretoriaToneTextClass } from '@/lib/diretoriaKpiTone';
import { DIRETORIA_SNAPSHOT_INDICATOR_IDS } from '@/lib/diretoriaDashboard';
import type { DiretoriaIndicator } from '@/data/diretoriaTypes';
import { DiretoriaModalChart } from '@/components/Diretoria/DiretoriaModalChart';
import {
  DiretoriaSqlButton,
  type DiretoriaSqlButtonProps,
} from '@/components/Diretoria/DiretoriaSqlButton';

export interface DiretoriaIndicatorAnalysisModalProps {
  indicator: DiretoriaIndicator | null;
  isOpen: boolean;
  onClose: () => void;
  sqlProps?: DiretoriaSqlButtonProps;
}

function looksNumericCell(value: string): boolean {
  const t = value.trim();
  if (!t || t === '—') return false;
  if (/^R\$\s?/.test(t)) return true;
  if (/%$/.test(t)) return true;
  if (/^[+-]?\d{1,3}(\.\d{3})*(,\d+)?$/.test(t)) return true;
  if (/^[+-]?\d+([.,]\d+)?$/.test(t)) return true;
  return false;
}

function columnAlignRight(rows: string[][], colIndex: number): boolean {
  if (rows.length === 0) return false;
  const sample = rows.slice(0, 12).map((r) => r[colIndex] ?? '');
  const numeric = sample.filter(looksNumericCell).length;
  return numeric >= Math.ceil(sample.length * 0.6);
}

export const DiretoriaIndicatorAnalysisModal: React.FC<DiretoriaIndicatorAnalysisModalProps> = ({
  indicator,
  isOpen,
  onClose,
  sqlProps,
}) => {
  const open = indicator;
  const isSnapshot = open ? DIRETORIA_SNAPSHOT_INDICATOR_IDS.has(open.id) : false;
  const chartHeight =
    open?.chartKind === 'pie' || open?.chartKind === 'barHorizontal' ? 360 : 320;
  const kpis = open?.kpis ?? [];
  const rightAlign = open
    ? open.tableHeaders.map((_, i) => columnAlignRight(open.tableRows ?? [], i))
    : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={open?.title ?? 'Indicador'}
      size="2xl"
    >
      {open ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="text-sm text-muted-foreground">{open.shortDescription}</p>
              {isSnapshot ? (
                <Badge variant="outline" className="w-fit text-[10px] font-normal">
                  Situação atual · não filtrado por período
                </Badge>
              ) : null}
            </div>
            {sqlProps ? <DiretoriaSqlButton {...sqlProps} /> : null}
          </div>

            {!open.hasData ? (
            <p className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
              Não foi possível carregar este indicador com o filtro atual. Verifique a conexão ERP, o Código ERP da
              empresa ou tente novamente em instantes.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {kpis.map((kpi) => (
                  <div
                    key={kpi.label}
                    className="rounded-lg border border-border/80 bg-muted/20 px-3 py-2.5"
                  >
                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {kpi.label}
                    </p>
                    <p
                      className={cn(
                        'mt-0.5 font-heading text-lg font-bold tabular-nums break-words sm:text-xl',
                        diretoriaToneTextClass(kpi.tone)
                      )}
                    >
                      {kpi.value}
                    </p>
                    {kpi.hint ? (
                      <p className="mt-1 text-[10px] text-muted-foreground">{kpi.hint}</p>
                    ) : null}
                  </div>
                ))}
              </div>

              {open.chartKind !== 'none' ? (
                <div className="space-y-2">
                  {open.chartTitle ? (
                    <h3 className="text-sm font-semibold text-foreground">{open.chartTitle}</h3>
                  ) : null}
                  <div className="rounded-lg border border-border/80 bg-background p-2 sm:p-3">
                    <DiretoriaModalChart indicator={open} height={chartHeight} pieUseLegend />
                  </div>
                </div>
              ) : null}

              <div className="max-h-72 overflow-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-[1]">
                    <tr className="border-b border-border bg-muted/80 text-left backdrop-blur-sm">
                      {open.tableHeaders.map((h, hi) => (
                        <th
                          key={h}
                          className={cn(
                            'px-3 py-2 font-medium text-foreground',
                            rightAlign[hi] && 'text-right'
                          )}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {open.tableRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={open.tableHeaders.length}
                          className="px-3 py-6 text-center text-muted-foreground"
                        >
                          Sem registros
                        </td>
                      </tr>
                    ) : (
                      open.tableRows.map((row, ri) => (
                        <tr key={ri} className="border-b border-border/80 last:border-0">
                          {row.map((cell, ci) => (
                            <td
                              key={ci}
                              className={cn(
                                'px-3 py-2 text-foreground',
                                rightAlign[ci] && 'text-right tabular-nums'
                              )}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <p className="border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground">
            {open.footnote}
          </p>
        </div>
      ) : null}
    </Modal>
  );
};
